// Internal modules
import { appStore } from '@/store';
import { apolloQuery } from '@/lib/apollo';
import { features, getLocale, getToken } from '@/lib';
import {
  getShippingAddress,
  getShippingCountry,
  getSimpleShippingAddressString,
  simplifyShippingAddress,
} from '@/lib/utils/shipping';
import { CALCULATE_SHIPPING_AND_TAXES_QUERY } from '../queries';
import type { CalcShippingAndTaxesQueryQuery } from '@/generated/graphql';
// @ts-expect-error
import { getShippingMethodsToStoreInState } from '../lenses/getShippingMethodsToStoreInState';
// @ts-expect-error
import { logMissingShippingMethodsToFPTI } from '../../fpti';
import { refetchCheckoutSession } from './refetchCheckoutSession';

const calculateShippingAndTaxes =
  (): Promise<void | CalcShippingAndTaxesQueryQuery> => {
    const { ui: uiActions } = appStore.getActions();
    const { setIsShippingShown, setIsShippingAndTaxLoaded } = uiActions;

    const { language: lang } = getLocale();
    const country = getShippingCountry();

    const variables = {
      locale: {
        country,
        lang,
      },
      shippingAddress: simplifyShippingAddress(
        getShippingAddress(),
        getShippingCountry(),
      ),
      token: getToken(),
    };

    return apolloQuery({
      query: CALCULATE_SHIPPING_AND_TAXES_QUERY,
      variables,
    }).then(({ data }) => {
      const { api: apiState, ui: uiState } = appStore.getState();
      const { pageLevelError } = uiState;
      const { api: apiActions, ui: uiActions } = appStore.getActions();
      const {
        setCartData,
        setShippingOptionsSource,
        setProfileOverrideAllowed,
      } = apiActions;
      const { setPageLevelError } = uiActions;
      const {
        charges,
        profileOverrideAllowed,
        shippingOptionsSource,
        isShippingRegionSupported,
      } = data.calculateShippingAndTaxes!;
      const isShippingMethodAvailable = charges.length > 0;

      setIsShippingAndTaxLoaded(true);

      if (!isShippingRegionSupported) {
        setPageLevelError('pageLevelError.shippingRegionUnsupported');
      } else if (
        isShippingRegionSupported &&
        pageLevelError === 'pageLevelError.shippingRegionUnsupported'
      ) {
        // clear page level error if there is an error for
        // shippingRegionUnsupported
        setPageLevelError('');
      }

      if (!isShippingMethodAvailable) {
        // If there are no available shipping options, but we previously had shipping or tax
        // set on the cart, we probably changed countries and need to clear the old shipping
        // and taxes off of the cart
        const prevTax =
          apiState?.cartDetails?.amounts?.tax?.currencyValue !== '0.00';
        const prevShip =
          apiState?.cartDetails?.amounts?.shipping?.currencyValue !== '0.00';
        logMissingShippingMethodsToFPTI();
        setIsShippingShown(false);
        if (prevTax || prevShip) {
          return refetchCheckoutSession();
        }

        return;
      }

      setCartData({
        calculatedShippingMethodsAddress: getSimpleShippingAddressString(
          variables.shippingAddress,
        ),
        shippingMethods: getShippingMethodsToStoreInState(charges),
      });
      setShippingOptionsSource(shippingOptionsSource);
      setProfileOverrideAllowed(profileOverrideAllowed);

      if (features.isActive('hideShipping')) {
        setIsShippingShown(false);
      }
      return data;
    });
  };

export { calculateShippingAndTaxes };
